import axios from "axios";
import { useEffect, useState } from "react";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  TextField,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import {
  PopperFullWidth,
  getInputLabelProps,
  getInputProps,
} from "../../../common/appStyles";
import localize from "../../../common/localize";
import Combobox from "../../../components/common/Combobox";
import {
  defaultNotBillingMotivation,
  getNotBillingMotivations,
} from "../../../common/EntitiesManagement";
import useGlobalContext from "../../../hooks/useGlobalContext";
import CreatedByText from "../../../components/common/CreatedByText";

export default function AddBilling({
  BillingModel,
  setBillingModel,
  alwaysBilled,
  billClient,
}) {
  const { CLIENTS_API, config } = useGlobalContext();
  const maximumCharactersForObservations = 255;
  const [descriptionCount, setDescriptionCount] = useState(0);
  const [selectedMotivation, setSelectedMotivation] = useState(
    defaultNotBillingMotivation()
  );
  const motivationOptions = getNotBillingMotivations();
  const [client, setClient] = useState(billClient);
  const [clientOptions, setClientOptions] = useState([]);

  useEffect(() => {
    if (!billClient) {
      getClients();
    }
  }, []);

  useEffect(() => {
    setBillingModel({
      ...BillingModel,
      Client: client,
      ClientId: client ? client.Id : 0,
    });
  }, [client]);

  const getClients = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${CLIENTS_API}/`, requestConfig);

    setClientOptions(data?.data?.Data);
  };

  const handleCodeChange = (value) => {
    setBillingModel({
      ...BillingModel,
      Code: value.target.value,
    });
  };

  useEffect(() => {
    setDescriptionCount(BillingModel.Observation.length);
  }, [BillingModel?.Observation]);

  const handleGenerationDateChange = (value) => {
    try {
      if (!value) {
        setBillingModel({
          ...BillingModel,
          GenerationDate: new Date(),
        });
        return;
      }

      setBillingModel({
        ...BillingModel,
        GenerationDate: value,
      });
    } catch {
      console.log("Invalid billing date");
    }
  };

  const handleObservationChange = (value) => {
    setBillingModel({
      ...BillingModel,
      Observation: value.target.value,
    });
  };

  const handleValueChange = (inputValue) => {
    setBillingModel({
      ...BillingModel,
      Value: inputValue.target.value,
    });
  };

  const handleBilledCheckedChanged = (value) => {
    setBillingModel({
      ...BillingModel,
      Billed: value.target.checked,
    });
  };

  const handleMotivationChange = (value) => {
    if (!value) {
      setSelectedMotivation(defaultNotBillingMotivation());
      return;
    }

    setSelectedMotivation(value);
  };

  useEffect(() => {
    setBillingModel({
      ...BillingModel,
      Observation:
        selectedMotivation.Id > 0
          ? selectedMotivation.Name
          : BillingModel.Observation,
    });
  }, [selectedMotivation]);

  const observationComponent = (isRequired) => (
    <div className="w-full mt-2">
      <FormControl required={isRequired} fullWidth>
        <TextField
          required={isRequired}
          InputLabelProps={getInputLabelProps()}
          inputProps={{
            style: {
              fontSize: "0.8em",
            },
            maxLength: maximumCharactersForObservations,
          }}
          size="small"
          fullWidth
          multiline
          rows={3}
          id="outlined-basic"
          name="Observation"
          label={localize.observations}
          variant="outlined"
          onChange={handleObservationChange}
          value={BillingModel.Observation}
          helperText={`${descriptionCount}/${maximumCharactersForObservations}`}
        />
      </FormControl>
    </div>
  );

  const handleClientChange = async (currentClient) => {
    setClient(currentClient);
  };

  return (
    <div style={{ width: "100%" }}>
      <form className="w-full">
        <div className="flex-col flex-wrap mb-3 flex gap-2">
          <FormControlLabel
            control={
              <Checkbox
                onChange={handleBilledCheckedChanged}
                checked={BillingModel.Billed}
              />
            }
            label={BillingModel.Billed ? localize.billed : localize.not_billed}
            disabled={alwaysBilled}
          />

          {BillingModel.Billed ? (
            <>
              <div className="w-full mt-3">
                <FormControl required fullWidth>
                  <TextField
                    required
                    InputLabelProps={getInputLabelProps()}
                    inputProps={getInputProps()}
                    size="small"
                    fullWidth
                    id="outlined-basic"
                    name="Name"
                    label={localize.bill_code}
                    variant="outlined"
                    onChange={handleCodeChange}
                    value={BillingModel.Code}
                  />
                </FormControl>
              </div>

              {billClient ? (
                <div className="w-full mt-3">
                  <FormControl required fullWidth>
                    <TextField
                      required
                      InputLabelProps={getInputLabelProps()}
                      inputProps={getInputProps()}
                      size="small"
                      fullWidth
                      id="outlined-basic"
                      name="Client"
                      label={localize.client}
                      variant="outlined"
                      disabled
                      value={`${billClient.Identification} - ${billClient.Name}`}
                    />
                  </FormControl>
                </div>
              ) : (
                <div className="w-full mt-3">
                  <Combobox
                    fullWidth
                    id="Client"
                    fieldLabel={localize.client}
                    fieldOptions={clientOptions ?? []}
                    fieldValue={BillingModel?.Client}
                    handleValueChange={handleClientChange}
                    popperFullWidth={PopperFullWidth}
                    customOptionLabel={(option) =>
                      `${option.Identification} - ${option.Name}`
                    }
                  />
                </div>
              )}

              <div className="w-full mt-3">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label={localize.bill_date}
                    className="w-full"
                    InputLabelProps={getInputLabelProps()}
                    inputProps={getInputProps()}
                    PopperComponent={PopperFullWidth}
                    value={BillingModel.GenerationDate}
                    onChange={handleGenerationDateChange}
                    maxDate={new Date()}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        inputProps={{
                          ...params.inputProps,
                          placeholder: "",
                        }}
                        InputLabelProps={{
                          ...params.InputLabelProps,
                          style: {
                            fontSize: "1em",
                            paddingBottom: "10px",
                          },
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>

                <div className="mt-3">
                  <TextField
                    type="number"
                    InputLabelProps={getInputLabelProps()}
                    inputProps={{
                      ...getInputProps(),
                      inputProps: {
                        min: 0,
                      },
                    }}
                    value={BillingModel.Value}
                    onChange={handleValueChange}
                    size="small"
                    id="Value"
                    name="Value"
                    label={localize.total_value}
                    variant="outlined"
                    fullWidth
                  />
                </div>
              </div>

              {observationComponent(false)}
            </>
          ) : (
            <div className="w-full mt-3">
              <Combobox
                isRequired
                fullWidth
                id="Motivation"
                fieldLabel={localize.motivation}
                fieldOptions={motivationOptions}
                fieldValue={selectedMotivation}
                handleValueChange={handleMotivationChange}
                popperFullWidth={PopperFullWidth}
              />

              {selectedMotivation.Id === 0 && observationComponent(true)}
            </div>
          )}
        </div>
      </form>

      <CreatedByText entity={BillingModel} />
    </div>
  );
}
